// The admin's Stripe key management - lives in the Stripe row's gear
// dialog on /settings/integrations. The server stores keys sealed and
// never echoes them; the form is replace-only.

import { useState } from "react";
import { useCurrentUser } from "../../../components/auth/CurrentUserContext";
import { isAdminRole } from "../../../lib/roles";
import { Skeleton, SkeletonGroup } from "../../../components/chrome/Skeleton";
import {
  useStripeConfig,
  useSaveStripeKeys,
  useRemoveStripeKeys,
  useTestStripeConnection,
  useRegisterStripeWebhook,
} from "../hooks/useStripeConfig";
import type { StripeConfigStatus } from "../../../api/integrations";
import { SECRET_ON_FILE } from "../../../components/integrations/secretOnFile";

function stripeStatusLine(s: StripeConfigStatus): string {
  if (s.unreadable) {
    return "The stored keys no longer decrypt - the server encryption key changed. Re-enter both keys.";
  }
  if (!s.configured) return "No Stripe keys - online payment is off.";
  const mode =
    s.mode === "live" ? "Live key - client charges are real." : "Test key - sandbox charges only.";
  const from = s.source === "db" ? "Saved in Settings." : "From the server environment.";
  return `${mode} ${from}`;
}

export function StripeKeysSection() {
  // Mounted from the admin-only gear, but the flag stands on its own:
  // wherever this section lands, a non-admin never issues the GET.
  const user = useCurrentUser();
  const stripeQ = useStripeConfig(isAdminRole(user?.role));
  const save = useSaveStripeKeys();
  const remove = useRemoveStripeKeys();
  const testConn = useTestStripeConnection();
  const registerHook = useRegisterStripeWebhook();
  const [secretKey, setSecretKey] = useState("");
  const [webhookSecret, setWebhookSecret] = useState("");

  const status = stripeQ.data;
  const canSave =
    Boolean(status?.canStore) &&
    (secretKey.trim() !== "" || webhookSecret.trim() !== "") &&
    !save.isPending;

  function onSave() {
    const body: { secretKey?: string; webhookSecret?: string } = {};
    if (secretKey.trim()) body.secretKey = secretKey.trim();
    if (webhookSecret.trim()) body.webhookSecret = webhookSecret.trim();
    // The probe and webhook verdicts belong to the keys that were tested, and
    // a save or a removal replaces those keys.
    testConn.reset();
    registerHook.reset();
    save.mutate(body, {
      onSuccess: () => {
        setSecretKey("");
        setWebhookSecret("");
      },
    });
  }

  function onRemove() {
    const ok = window.confirm(
      "Remove the stored Stripe keys? Online payment stops until new keys are saved; " +
        "keys from the server environment, if any, apply again.",
    );
    if (!ok) return;
    testConn.reset();
    registerHook.reset();
    save.reset();
    remove.mutate();
  }

  function onRegisterWebhook() {
    const ok = window.confirm(
      "Register the Stripe webhook for this app? An existing endpoint on the same URL is " +
        "replaced, and the new signing secret is stored automatically.",
    );
    if (!ok) return;
    registerHook.mutate();
  }

  return (
    <section className="settings-section">
      <p className="settings-sub">
        Stripe keys for client checkout. Stored encrypted on the server and never shown back - enter
        a value to replace the one on file.
      </p>

      {stripeQ.isLoading && (
        <SkeletonGroup>
          <Skeleton width={220} />
          <Skeleton width={180} />
        </SkeletonGroup>
      )}
      {stripeQ.isError && (
        <p className="settings-error">Could not load the payment settings. Refresh to retry.</p>
      )}

      {status && (
        <>
          <p className="settings-sub" role="status">
            {stripeStatusLine(status)}
          </p>
          {status.configured && !status.webhookSecretSet && (
            <p className="settings-warn">No webhook secret - payments will not complete.</p>
          )}
          {status.source === "db" && status.updatedAt && (
            <p className="settings-hint">
              Saved {new Date(status.updatedAt).toLocaleDateString()}
              {status.updatedByEmail ? ` by ${status.updatedByEmail}` : ""}
            </p>
          )}
          {!status.canStore && (
            <p className="settings-error">
              Key storage is off - set ENCRYPTION_KEY on the server to save keys here.
            </p>
          )}
        </>
      )}

      <div className="settings-grid">
        <label className="settings-field">
          <span className="settings-label">Secret key</span>
          <input
            className="cc-field"
            type="password"
            autoComplete="new-password"
            placeholder={status?.configured ? SECRET_ON_FILE : "sk_live_... or sk_test_..."}
            value={secretKey}
            onChange={(e) => setSecretKey(e.target.value)}
          />
          {status?.configured && (
            <span className="settings-hint">A key is on file - saving replaces it.</span>
          )}
        </label>
        <label className="settings-field">
          <span className="settings-label">Webhook signing secret</span>
          <input
            className="cc-field"
            type="password"
            autoComplete="new-password"
            placeholder={status?.webhookSecretSet ? SECRET_ON_FILE : "whsec_..."}
            value={webhookSecret}
            onChange={(e) => setWebhookSecret(e.target.value)}
          />
          {status?.webhookSecretSet && (
            <span className="settings-hint">A secret is on file - saving replaces it.</span>
          )}
        </label>
      </div>

      <div className="settings-actions">
        <button type="button" className="settings-save-btn" disabled={!canSave} onClick={onSave}>
          {save.isPending ? "Saving..." : "Save keys"}
        </button>
        {status?.source === "db" && (
          <button
            type="button"
            className="cc-link-btn danger"
            disabled={remove.isPending}
            onClick={onRemove}
          >
            Remove keys
          </button>
        )}
        {status?.configured && (
          <button
            type="button"
            className="cc-link-btn"
            disabled={testConn.isPending}
            onClick={() => testConn.mutate()}
          >
            {testConn.isPending ? "Testing..." : "Test connection"}
          </button>
        )}
        {status?.configured && status.canStore && (
          <button
            type="button"
            className="cc-link-btn"
            disabled={registerHook.isPending}
            onClick={onRegisterWebhook}
          >
            {registerHook.isPending ? "Registering..." : "Register webhook"}
          </button>
        )}
      </div>

      {save.isError && <p className="settings-error">{(save.error as Error).message}</p>}
      {remove.isError && <p className="settings-error">{(remove.error as Error).message}</p>}
      {save.isSuccess && save.data?.warning && <p className="settings-warn">{save.data.warning}</p>}
      {save.isSuccess && !save.data?.warning && (
        <p className="settings-ok" role="status">
          Keys saved.
        </p>
      )}
      {testConn.data && testConn.data.ok && (
        <p
          className={testConn.data.chargesEnabled === false ? "settings-warn" : "settings-ok"}
          role="status"
        >
          Connected as {testConn.data.accountId}
          {testConn.data.chargesEnabled === false ? " - charges are disabled on this account" : ""}
        </p>
      )}
      {testConn.data && !testConn.data.ok && (
        <p className="settings-error">Stripe rejected the key ({testConn.data.errorCode}).</p>
      )}
      {testConn.isError && <p className="settings-error">{(testConn.error as Error).message}</p>}
      {registerHook.isSuccess && (
        <p className="settings-ok" role="status">
          Webhook {registerHook.data.replaced ? "replaced" : "registered"} at{" "}
          {registerHook.data.webhookUrl}.
        </p>
      )}
      {registerHook.isError && (
        <p className="settings-error">{(registerHook.error as Error).message}</p>
      )}
    </section>
  );
}
